import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalendarIcon, Plus, MapPin, Clock, Users, Video, Trash2,
  Search, UserCheck, CheckCircle2, Loader2, ChevronRight, AlertTriangle,
  Filter, TrendingUp, Play, X, ExternalLink, UserPlus, FileText,
  RefreshCw, DollarSign, XCircle, Send
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const statusColors = {
  requested: "bg-purple-50 text-purple-700 border-purple-200",
  confirmed: "bg-teal-50 text-teal-700 border-teal-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const SessionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    course_id: "", title: "", description: "", location: "",
    location_type: "physical", virtual_link: "", session_date: "",
    start_time: "09:00", end_time: "17:00", capacity: 30,
    instructor_name: ""
  });

  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [attendeeSession, setAttendeeSession] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [attendeeSearch, setAttendeeSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [attendeeLoading, setAttendeeLoading] = useState(false);
  const [addingAttendees, setAddingAttendees] = useState(false);

  // Reject with reason
  const [showReject, setShowReject] = useState(false);
  const [rejectSession, setRejectSession] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  // Reschedule
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleSession, setRescheduleSession] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ session_date: "", start_time: "", end_time: "", location: "", reason: "" });
  const [rescheduling, setRescheduling] = useState(false);

  // Purchase Order
  const [showPO, setShowPO] = useState(false);
  const [poSession, setPOSession] = useState(null);
  const [poForm, setPOForm] = useState({ po_number: "", po_amount: 0, po_vendor: "", po_notes: "", po_status: "pending" });
  const [poSaving, setPOSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, cRes, dRes] = await Promise.all([
        api.get("/sessions"),
        api.get("/courses"),
        api.get("/sessions/dashboard"),
      ]);
      setSessions(sRes.data || []);
      setCourses((cRes.data?.courses || cRes.data || []).filter(c => c.course_type === "ilt"));
      setDashboard(dRes.data);
    } catch (e) { toast.error("Failed to load sessions"); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.course_id || !form.title || !form.session_date) {
      toast.error("Please fill required fields"); return;
    }
    setSaving(true);
    try {
      await api.post("/sessions", form);
      toast.success("Session created");
      setShowCreate(false); resetForm(); fetchAll();
    } catch (e) { toast.error("Failed to create session"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session deleted"); fetchAll();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/sessions/${id}`, { status: newStatus });
      toast.success("Status updated"); fetchAll();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleConfirmSession = async (id) => {
    try {
      await api.put(`/sessions/${id}/confirm`);
      toast.success("Session confirmed"); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to confirm"); }
  };

  const handleRejectSession = async (id) => {
    // Open reject dialog instead of direct reject
    const session = sessions.find(s => s.id === id);
    setRejectSession(session);
    setRejectReason("");
    setShowReject(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) { toast.error("Please provide a reason for rejection"); return; }
    setRejecting(true);
    try {
      await api.put(`/sessions/${rejectSession.id}/reject`, { reason: rejectReason });
      toast.success("Session rejected"); setShowReject(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
    setRejecting(false);
  };

  const openReschedule = (session) => {
    setRescheduleSession(session);
    setRescheduleForm({ session_date: session.session_date || "", start_time: session.start_time || "", end_time: session.end_time || "", location: session.location || "", reason: "" });
    setShowReschedule(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.reason.trim()) { toast.error("Please provide a reason for rescheduling"); return; }
    setRescheduling(true);
    try {
      await api.post(`/sessions/${rescheduleSession.id}/reschedule`, rescheduleForm);
      toast.success("Session rescheduled"); setShowReschedule(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reschedule"); }
    setRescheduling(false);
  };

  const openPurchaseOrder = async (session) => {
    setPOSession(session);
    setPOForm({ po_number: "", po_amount: 0, po_vendor: "", po_notes: "", po_status: "pending" });
    try {
      const res = await api.get(`/sessions/${session.id}/purchase-order`);
      if (res.data && res.data.po_number) setPOForm(res.data);
    } catch {}
    setShowPO(true);
  };

  const handleSavePO = async () => {
    if (!poForm.po_number.trim()) { toast.error("PO number is required"); return; }
    setPOSaving(true);
    try {
      await api.post(`/sessions/${poSession.id}/purchase-order`, poForm);
      toast.success("Purchase order saved"); setShowPO(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setPOSaving(false);
  };

  const openAttendance = async (session) => {
    setSelectedSession(session);
    try {
      const res = await api.get(`/sessions/${session.id}/registrations`);
      setRegistrations(res.data || []);
    } catch (e) { setRegistrations([]); }
    setShowAttendance(true);
  };

  const markAttendance = async (attendanceList) => {
    try {
      await api.put(`/sessions/${selectedSession.id}/attendance`, attendanceList);
      toast.success("Attendance marked"); openAttendance(selectedSession);
    } catch (e) { toast.error("Failed to mark attendance"); }
  };

  const resetForm = () => setForm({
    course_id: "", title: "", description: "", location: "",
    location_type: "physical", virtual_link: "", session_date: "",
    start_time: "09:00", end_time: "17:00", capacity: 30, instructor_name: ""
  });

  const openAddAttendee = async (session) => {
    setAttendeeSession(session);
    setSelectedUserIds([]);
    setAttendeeSearch("");
    setShowAddAttendee(true);
    setAttendeeLoading(true);
    try {
      const res = await api.get(`/sessions/${session.id}/available-users`);
      setAvailableUsers(res.data || []);
    } catch { setAvailableUsers([]); }
    setAttendeeLoading(false);
  };

  const searchAvailableUsers = async (q) => {
    setAttendeeSearch(q);
    if (!attendeeSession) return;
    setAttendeeLoading(true);
    try {
      const res = await api.get(`/sessions/${attendeeSession.id}/available-users?q=${encodeURIComponent(q)}`);
      setAvailableUsers(res.data || []);
    } catch { setAvailableUsers([]); }
    setAttendeeLoading(false);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleAddAttendees = async () => {
    if (selectedUserIds.length === 0) { toast.error("Select at least one user"); return; }
    setAddingAttendees(true);
    try {
      const res = await api.post(`/sessions/${attendeeSession.id}/add-attendees`, { user_ids: selectedUserIds });
      toast.success(`${res.data.added} attendee(s) added`);
      setShowAddAttendee(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add attendees"); }
    setAddingAttendees(false);
  };

  const today = new Date().toISOString().split("T")[0];

  const filteredSessions = useMemo(() => {
    let list = sessions;
    if (activeTab === "active") list = list.filter(s => ["confirmed", "scheduled", "in_progress"].includes(s.status) && s.session_date >= today);
    else if (activeTab === "pending") list = list.filter(s => s.status === "requested");
    else if (activeTab === "upcoming") list = list.filter(s => s.session_date >= today && !["completed", "cancelled", "rejected"].includes(s.status));
    else if (activeTab === "completed") list = list.filter(s => s.status === "completed");
    else if (activeTab === "cancelled") list = list.filter(s => ["cancelled", "rejected"].includes(s.status));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s => s.title.toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q) || (s.instructor_name || "").toLowerCase().includes(q));
    }
    return list;
  }, [sessions, activeTab, search, today]);

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const d = dashboard || { total: 0, upcoming: 0, completed: 0, in_progress: 0, cancelled: 0, attendance_rate: 0 };

  if (loading) return (
    <AdminLayout title="ILT Sessions" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="ILT Sessions" variant={variant}>
      <div className="space-y-5" data-testid="sessions-page">

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border shadow-none lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold tracking-tight">Training Sessions Overview</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Instructor-led training sessions across the organization</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-primary">{d.total}</p>
                  <p className="text-[10px] text-muted-foreground">Total Sessions</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Upcoming", value: d.upcoming, icon: CalendarIcon, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "In Progress", value: d.in_progress, icon: Play, color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Completed", value: d.completed, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
                  { label: "Cancelled", value: d.cancelled, icon: X, color: "text-red-600", bg: "bg-red-50" },
                ].map((st, i) => (
                  <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${st.bg}`}>
                    <st.icon className={`w-4 h-4 ${st.color} shrink-0`} />
                    <div>
                      <p className="text-lg font-bold leading-none">{st.value}</p>
                      <p className="text-[10px] text-muted-foreground">{st.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border shadow-none">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" /> Session Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-3">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Attendance Rate</span>
                  <span className="text-xs font-bold">{d.attendance_rate}%</span>
                </div>
                <Progress value={d.attendance_rate} className="h-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="p-2.5 rounded-lg bg-muted/30">
                  <p className="text-lg font-bold text-primary">{sessions.filter(s => s.location_type === "virtual").length}</p>
                  <p className="text-[10px] text-muted-foreground">Virtual</p>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30">
                  <p className="text-lg font-bold text-primary">{sessions.filter(s => s.location_type === "physical").length}</p>
                  <p className="text-[10px] text-muted-foreground">In-Person</p>
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30">
                <p className="text-lg font-bold text-primary">{sessions.reduce((sum, s) => sum + (s.enrolled_count || 0), 0)}</p>
                <p className="text-[10px] text-muted-foreground">Total Registrations</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs + Search + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="active" className="text-xs gap-1.5" data-testid="tab-active">
                <Play className="w-3.5 h-3.5" /> Active
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{sessions.filter(s => ["confirmed", "scheduled", "in_progress"].includes(s.status) && s.session_date >= today).length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs gap-1.5" data-testid="tab-pending">
                <AlertTriangle className="w-3.5 h-3.5" /> Pending Approval
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4 bg-purple-100 text-purple-700">{sessions.filter(s => s.status === "requested").length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="text-xs gap-1.5" data-testid="tab-upcoming">
                <CalendarIcon className="w-3.5 h-3.5" /> Upcoming
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{d.upcoming}</Badge>
              </TabsTrigger>
              <TabsTrigger value="completed" className="text-xs gap-1.5" data-testid="tab-completed">
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{d.completed}</Badge>
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs gap-1.5" data-testid="tab-all">
                <Filter className="w-3.5 h-3.5" /> All
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{d.total}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search sessions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-56 text-sm" data-testid="search-input" />
            </div>
            <Button onClick={() => { resetForm(); setShowCreate(true); }} className="h-9 text-xs gap-1.5" data-testid="create-session-btn">
              <Plus className="w-4 h-4" /> New Session
            </Button>
          </div>
        </div>

        {/* Sessions List */}
        <div className="space-y-3" data-testid="sessions-list">
          <AnimatePresence>
            {filteredSessions.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {activeTab === "completed" ? "No completed sessions yet" :
                   activeTab === "cancelled" ? "No cancelled sessions" :
                   "No sessions match your search. Create your first ILT session."}
                </p>
              </div>
            ) : filteredSessions.map(session => (
              <motion.div key={session.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
                <Card
                  className={`border shadow-none transition-all hover:shadow-sm group cursor-pointer ${
                    session.status === "completed" ? "border-green-200 bg-green-50/20" :
                    session.status === "in_progress" ? "border-amber-200 bg-amber-50/10" :
                    ["cancelled", "rejected"].includes(session.status) ? "border-red-200 bg-red-50/10" :
                    "hover:border-primary/30"
                  }`}
                  data-testid={`session-card-${session.id}`}
                  onClick={() => {
                    const base = user?.role === "super_admin" ? "/admin" : user?.role === "tenant_admin" ? "/tenant" : "/learner";
                    navigate(`${base}/sessions/${session.id}`);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Status icon */}
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                        session.status === "completed" ? "bg-green-100" :
                        session.status === "in_progress" ? "bg-amber-100" :
                        session.status === "requested" ? "bg-purple-100" :
                        ["cancelled", "rejected"].includes(session.status) ? "bg-red-100" :
                        "bg-primary/10"
                      }`}>
                        {session.status === "completed" ? <CheckCircle2 className="w-5 h-5 text-green-600" /> :
                         session.status === "in_progress" ? <Play className="w-5 h-5 text-amber-600" /> :
                         session.status === "requested" ? <AlertTriangle className="w-5 h-5 text-purple-600" /> :
                         ["cancelled", "rejected"].includes(session.status) ? <X className="w-5 h-5 text-red-500" /> :
                         <CalendarIcon className="w-5 h-5 text-primary" />}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="text-sm font-semibold group-hover:text-primary transition-colors">{session.title}</h4>
                          <Badge className={`text-[9px] px-1.5 py-0 border ${statusColors[session.status]}`}>{session.status.replace("_", " ")}</Badge>
                          <Badge variant="outline" className="text-[9px]">{session.location_type}</Badge>
                        </div>
                        {session.description && <p className="text-xs text-muted-foreground line-clamp-1">{session.description}</p>}
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{session.session_date}</span>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{session.start_time} - {session.end_time}</span>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            {session.location_type === "virtual" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                            {session.location || "TBD"}
                          </span>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" />{session.enrolled_count || 0}/{session.capacity}</span>
                          {session.instructor_name && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><UserCheck className="w-3 h-3" />{session.instructor_name}</span>}
                        </div>
                      </div>

                      {/* Capacity + Actions */}
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center gap-1.5 text-[10px]">
                            <span className="text-muted-foreground">{session.enrolled_count || 0}/{session.capacity} enrolled</span>
                          </div>
                          <div className="w-24 mt-1.5">
                            <Progress value={session.capacity > 0 ? ((session.enrolled_count || 0) / session.capacity * 100) : 0} className="h-1.5" />
                          </div>
                        </div>

                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => openAttendance(session)} data-testid={`attendance-btn-${session.id}`}>
                            <UserCheck className="w-3.5 h-3.5" /> Attendance
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => openAddAttendee(session)} data-testid={`add-attendee-btn-${session.id}`}>
                            <UserPlus className="w-3.5 h-3.5" /> Add Attendee
                          </Button>
                          {session.status === "requested" && user?.role === "super_admin" && (
                            <>
                              <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700 gap-1" onClick={() => handleConfirmSession(session.id)} data-testid={`confirm-btn-${session.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 text-xs text-destructive border-destructive/30 gap-1" onClick={() => handleRejectSession(session.id)} data-testid={`reject-btn-${session.id}`}>
                                <XCircle className="w-3.5 h-3.5" /> Reject
                              </Button>
                            </>
                          )}
                          {session.status === "requested" && user?.role !== "super_admin" && (
                            <Badge variant="outline" className="text-purple-600 border-purple-500/30 text-[9px]">Awaiting Approval</Badge>
                          )}
                          {["confirmed", "scheduled"].includes(session.status) && (
                            <>
                              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => openReschedule(session)} data-testid={`reschedule-btn-${session.id}`}>
                                <RefreshCw className="w-3.5 h-3.5" /> Reschedule
                              </Button>
                              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => openPurchaseOrder(session)} data-testid={`po-btn-${session.id}`}>
                                <FileText className="w-3.5 h-3.5" /> PO
                              </Button>
                              <Select value="" onValueChange={(v) => handleStatusChange(session.id, v)}>
                                <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue placeholder="Set Status" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="in_progress">Start</SelectItem>
                                  <SelectItem value="cancelled">Cancel</SelectItem>
                                </SelectContent>
                              </Select>
                            </>
                          )}
                          {session.status === "in_progress" && (
                            <>
                              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => openPurchaseOrder(session)} data-testid={`po-btn-${session.id}`}>
                                <FileText className="w-3.5 h-3.5" /> PO
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => handleStatusChange(session.id, "completed")}>
                                <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleDelete(session.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Create Session Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create ILT Session</DialogTitle>
            <DialogDescription>Schedule a new instructor-led training session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto px-1 pr-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Course *</Label>
              <Select value={form.course_id} onValueChange={v => setForm({...form, course_id: v})}>
                <SelectTrigger className="h-9 text-xs" data-testid="session-course-select"><SelectValue placeholder="Select ILT course" /></SelectTrigger>
                <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id} className="text-xs">{c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Session Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. March Cohort - Electrical Safety" className="h-9 text-sm" data-testid="session-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" value={form.session_date} onChange={e => setForm({...form, session_date: e.target.value})} className="h-9 text-sm" data-testid="session-date-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value)||30})} className="h-9 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Location Type</Label>
              <Select value={form.location_type} onValueChange={v => setForm({...form, location_type: v})}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical" className="text-xs">Physical</SelectItem>
                  <SelectItem value="virtual" className="text-xs">Virtual</SelectItem>
                  <SelectItem value="hybrid" className="text-xs">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Location / Room</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Training Room A" className="h-9 text-sm" /></div>
            {(form.location_type === "virtual" || form.location_type === "hybrid") && (
              <div className="space-y-1.5"><Label className="text-xs">Virtual Link</Label><Input value={form.virtual_link} onChange={e => setForm({...form, virtual_link: e.target.value})} placeholder="https://zoom.us/..." className="h-9 text-sm" /></div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Instructor Name</Label><Input value={form.instructor_name} onChange={e => setForm({...form, instructor_name: e.target.value})} placeholder="Instructor name" className="h-9 text-sm" /></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-session-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={showAttendance} onOpenChange={setShowAttendance}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attendance - {selectedSession?.title}</DialogTitle>
            <DialogDescription>{selectedSession?.session_date} | {selectedSession?.start_time} - {selectedSession?.end_time}</DialogDescription>
          </DialogHeader>
          {registrations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No registrations yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {registrations.map(reg => (
                <div key={reg.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm">{reg.user_name || "Unknown"}</p>
                    <p className="text-xs text-muted-foreground">{reg.user_email}</p>
                  </div>
                  <Select value={reg.status} onValueChange={v => markAttendance([{ registration_id: reg.id, status: v }])}>
                    <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="registered" className="text-xs">Registered</SelectItem>
                      <SelectItem value="attended" className="text-xs">Attended</SelectItem>
                      <SelectItem value="absent" className="text-xs">Absent</SelectItem>
                      <SelectItem value="excused" className="text-xs">Excused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttendance(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add Attendee Dialog */}
      <Dialog open={showAddAttendee} onOpenChange={setShowAddAttendee}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Attendees - {attendeeSession?.title}</DialogTitle>
            <DialogDescription>Search and select users to register for this session</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by name or email..." value={attendeeSearch} onChange={e => searchAvailableUsers(e.target.value)} className="pl-9 h-9 text-sm" data-testid="attendee-search-input" />
            </div>
            {selectedUserIds.length > 0 && (
              <Badge variant="outline" className="text-xs">{selectedUserIds.length} selected</Badge>
            )}
            <div className="max-h-[40vh] overflow-y-auto space-y-1.5">
              {attendeeLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : availableUsers.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">No available users found</div>
              ) : availableUsers.map(u => (
                <label key={u.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedUserIds.includes(u.id) ? "border-primary bg-primary/5" : "hover:bg-muted/30"}`} data-testid={`user-option-${u.id}`}>
                  <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUserSelection(u.id)} className="rounded w-4 h-4" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{u.full_name}</p>
                    <p className="text-[10px] text-muted-foreground">{u.email} &middot; {u.role}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowAddAttendee(false)}>Cancel</Button>
            <Button onClick={handleAddAttendees} disabled={addingAttendees || selectedUserIds.length === 0} data-testid="confirm-add-attendees-btn">
              {addingAttendees && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add {selectedUserIds.length} Attendee{selectedUserIds.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Session Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Session Request</DialogTitle>
            <DialogDescription>Rejecting: {rejectSession?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-xs text-red-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />This will notify the requesting admin that their session has been declined.</p>
            </div>
            <div>
              <Label className="text-xs">Reason for Rejection *</Label>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="text-sm" placeholder="Provide a reason..." data-testid="reject-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1 bg-red-600 hover:bg-red-700" onClick={handleConfirmReject} disabled={rejecting} data-testid="confirm-reject-btn">
              {rejecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Reject Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Session</DialogTitle>
            <DialogDescription>Rescheduling: {rescheduleSession?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">New Date *</Label><Input type="date" value={rescheduleForm.session_date} onChange={e => setRescheduleForm({...rescheduleForm, session_date: e.target.value})} className="h-8 text-sm" data-testid="reschedule-date" /></div>
              <div><Label className="text-xs">Location</Label><Input value={rescheduleForm.location} onChange={e => setRescheduleForm({...rescheduleForm, location: e.target.value})} className="h-8 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start Time</Label><Input type="time" value={rescheduleForm.start_time} onChange={e => setRescheduleForm({...rescheduleForm, start_time: e.target.value})} className="h-8 text-sm" /></div>
              <div><Label className="text-xs">End Time</Label><Input type="time" value={rescheduleForm.end_time} onChange={e => setRescheduleForm({...rescheduleForm, end_time: e.target.value})} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">Reason for Rescheduling *</Label><Textarea value={rescheduleForm.reason} onChange={e => setRescheduleForm({...rescheduleForm, reason: e.target.value})} rows={2} className="text-sm" placeholder="Instructor unavailability, venue change..." data-testid="reschedule-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowReschedule(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleReschedule} disabled={rescheduling} data-testid="confirm-reschedule-btn">
              {rescheduling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Order Dialog */}
      <Dialog open={showPO} onOpenChange={setShowPO}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Purchase Order</DialogTitle>
            <DialogDescription>{poSession?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">PO Number *</Label><Input value={poForm.po_number} onChange={e => setPOForm({...poForm, po_number: e.target.value})} className="h-8 text-sm" placeholder="PO-2026-001" data-testid="po-number-input" /></div>
              <div><Label className="text-xs">Amount ($)</Label><Input type="number" value={poForm.po_amount} onChange={e => setPOForm({...poForm, po_amount: parseFloat(e.target.value)||0})} className="h-8 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Vendor</Label><Input value={poForm.po_vendor} onChange={e => setPOForm({...poForm, po_vendor: e.target.value})} className="h-8 text-sm" placeholder="Training vendor name" /></div>
              <div><Label className="text-xs">Status</Label>
                <Select value={poForm.po_status} onValueChange={v => setPOForm({...poForm, po_status: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending" className="text-xs">Pending</SelectItem>
                    <SelectItem value="approved" className="text-xs">Approved</SelectItem>
                    <SelectItem value="paid" className="text-xs">Paid</SelectItem>
                    <SelectItem value="cancelled" className="text-xs">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={poForm.po_notes} onChange={e => setPOForm({...poForm, po_notes: e.target.value})} rows={2} className="text-sm" placeholder="Additional PO details..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowPO(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleSavePO} disabled={poSaving} data-testid="save-po-btn">
              {poSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />} Save PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default SessionsPage;
